import classNames from 'classnames';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useTransition,
} from 'react';

import { TourClasses } from '@revelio/core';

import { usePageState } from '../PageState/PageStateProvider';
import { resolveActiveTab } from '../PageState/resolveActiveTab';
import styles from './TabManager.module.css';
import { useGetTabs, useSyncResolvedActiveTab, useUpdateTab } from './useTabs';

export const TabManager = () => {
  const tabs = useGetTabs();
  const { pageState } = usePageState();
  const updateTab = useUpdateTab();

  useSyncResolvedActiveTab();

  const [isPending, startTransition] = useTransition();
  const [pendingTabId, setPendingTabId] = useState<string | null>(null);

  useEffect(() => {
    if (!isPending) setPendingTabId(null);
  }, [isPending]);

  const resolvedTab = resolveActiveTab(tabs, pageState.activeTabId);
  const activeTabId =
    isPending && pendingTabId !== null ? pendingTabId : resolvedTab?.id;
  const activeIndex = activeTabId
    ? tabs.findIndex((tab) => tab.id === activeTabId)
    : 0;

  const tablistRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const tablist = tablistRef.current;
    const slider = sliderRef.current;
    if (!tablist || !slider) return;

    const tabButtons = tablist.querySelectorAll<HTMLElement>('[role="tab"]');
    const activeTab = tabButtons[activeIndex >= 0 ? activeIndex : 0];
    if (!activeTab) return;

    slider.style.transform = `translate3d(${activeTab.offsetLeft}px, 0, 0)`;
    slider.style.width = `${activeTab.offsetWidth}px`;
  }, [activeIndex]);

  const handleTabChange = (tabId: string) => {
    setPendingTabId(tabId);
    startTransition(() => {
      updateTab(tabId);
    });
  };

  if (tabs.length === 1) return null;

  return (
    <div className={classNames(styles.tabManager, TourClasses.TOUR_VIEW_CLASS)}>
      <div role="tablist" ref={tablistRef} className={styles.container}>
        <div ref={sliderRef} aria-hidden className={styles.slider} />
        {tabs.map((tab, i) => {
          const isActive = activeIndex === i;
          return (
            <button
              key={tab.id}
              role="tab"
              type="button"
              aria-selected={isActive}
              title={tab.label}
              onClick={() => handleTabChange(tab.id)}
              className={classNames(styles.tab, isActive && styles.tabActive)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};
